import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { object, string } from 'yup';
import { Formik, FormikProps, Form } from 'formik';
import styled from 'styled-components';

// hooks
import useBoolean from 'hooks/useBoolean';

// actions
import { updateItemTitle, ItemType, updateTemplateTitle } from 'ducks/merchantDucks/items/actions';
import { updateVoucherTitle } from 'ducks/merchantDucks/vouchers/actions';

// components
import ItemTitle from 'pages/MerchantPages/Assets/components/Navbar/ItemTitle';
import ItemPreviewDefaultTheme from 'pages/MerchantPages/Assets/components/Navbar/ItemPreviewDefaultBrandingTheme';
import ItemSync from 'pages/MerchantPages/Assets/components/Navbar/ItemSync';
import ItemEmbed from 'pages/MerchantPages/Assets/components/Navbar/ItemEmbed';
import ItemAnalytics from 'pages/MerchantPages/Assets/components/Navbar/ItemAnalytics';
import ItemDelete from 'pages/MerchantPages/Assets/components/Navbar/ItemDelete';

const ContainerStyled = styled.div`
  padding: 0.7rem;
  justify-content: space-between;
  display: flex;
`;

const ButtonWrapper = styled.div`
  margin: 0 1rem;
`;

interface FormValues {
  title: string;
}

interface OwnProps {
  title: string;
  itemType: string;
  itemId: number;
  isAnalyticsView?: boolean;
  toggleAnalyticsView?: () => void;
}

const validationSchema = object().shape({
  title: string().required('*Required').min(3).max(191),
});

type Props = OwnProps;

const NavbarForm: FC<Props> = ({ title, itemType, itemId, isAnalyticsView, toggleAnalyticsView }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isEditTemplate = pathname.includes('templates');
  const showTitleWithIcon = useBoolean(true);

  const handleHideTitleWithIcon = () => showTitleWithIcon.setFalse();

  const onFormSubmit = async (data: FormValues) => {
    if (itemType === ItemType.Voucher) {
      await dispatch(updateVoucherTitle(data.title, itemId));
      showTitleWithIcon.setTrue();
      return;
    }
    if (isEditTemplate) {
      await dispatch(updateTemplateTitle(data.title));
      showTitleWithIcon.setTrue();
      return;
    }

    await dispatch(updateItemTitle(data.title, itemId, itemType));
    showTitleWithIcon.setTrue();
  };

  const initialValues = {
    title: title || '',
  };

  const isAsset = itemType.includes('asset');
  const showAssetIcons = isAsset && !isEditTemplate;
  const isNotVoucher = itemType !== ItemType.Voucher;
  const showAnalyticsIcon = isNotVoucher && !isEditTemplate;
  const showDeleteIcon = !isEditTemplate;

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onFormSubmit}
      >
        {({ submitForm }: FormikProps<FormValues>) => {
          return (
            <Form>
              <ContainerStyled>
                <ItemTitle
                  title={title}
                  itemType={itemType}
                  saveTitle={submitForm}
                  showTitleWithIcon={showTitleWithIcon.value}
                  hideTitleWithIcon={handleHideTitleWithIcon}
                />
                <ButtonWrapper>
                  {showAssetIcons && (
                    <>
                      <ItemPreviewDefaultTheme />
                      <ItemSync />
                      <ItemEmbed />
                    </>
                  )}
                  {showAnalyticsIcon && (
                    <ItemAnalytics
                      itemType={itemType}
                      isAnalyticsView={isAnalyticsView}
                      toggleAnalyticsView={toggleAnalyticsView}
                    />
                  )}
                  {showDeleteIcon && <ItemDelete itemType={itemType} itemId={itemId} />}
                </ButtonWrapper>
              </ContainerStyled>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default NavbarForm;
